import React, { useEffect, useMemo, useState } from 'react';
import { AppBarCMS } from '../../../../basic';
import { IconButton } from '../../../../common/controls/Controls';
import { Spinner, CollapseBlock } from '../../../../common';
import { routes } from '../../../../../utils/helpers/ApiClient';
import ReservationsTable from './ReservationsTable';
import { useTranslation } from 'react-i18next';
import TableFilter from '../../../../common/TableFilter/TableFilter.jsx';
import { QuestionModal } from '../../../../common';
import { useSelector } from 'react-redux';
import {
  DeleteOutlined,
  PlusSquareOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteFilled,
  UploadOutlined,
} from '@ant-design/icons';
import {
  useGetReservationsQuery,
  useDeleteReservationMutation,
  useDeleteReservationSequenceMutation,
  useGetReservationStateChangeInCmsMutation,
  useImportReservationsMutation,
} from '../../../../../redux/services/reservations';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import { RESERVATION_STATUS } from '../../../../../utils/constants/enums.js';
import { Refresh_time } from '../../../../../utils/constants/refresh_time.js';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import {
  ROLE_DEPARTMENT_RESERVATION_RESPONSIBLE,
  ROLE_ROOM_RESERVATIONS_ADMIN,
} from '../../../../../utils/constants/roles.js';
import ReservationsImport from './ReservationsImport/ReservationsImport.jsx';

const DELETE_CONFIRM_CLOSE = -1;
const TABLE_FILTER_TITLE = 'Filter rezervácií';
const IMPORT_ERROR_TOAST_KEY = 'reservations-import-error';

const ReservationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isRoomReservationsAdmin = useUserHasRole(ROLE_ROOM_RESERVATIONS_ADMIN);
  const isDepartmentReservationResponsible = useUserHasRole(
    ROLE_DEPARTMENT_RESERVATION_RESPONSIBLE,
  );
  const canSeeForApproval =
    isDepartmentReservationResponsible || isRoomReservationsAdmin;
  const user = useSelector((reduxStore) => reduxStore.auth.user);

  const [approveId, setApproveId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [approveIsSequence, setApproveIsSequence] = useState(false);
  const [rejectIsSequence, setRejectIsSequence] = useState(false);
  const [confirmDeleteIndex, setDeleteIndex] = useState(DELETE_CONFIRM_CLOSE);
  const [confirmDeleteSequentialIndex, setDeleteSequentialIndex] =
    useState(DELETE_CONFIRM_CLOSE);
  const [confirmApproveAllVisible, setConfirmApproveAllVisible] =
    useState(false);

  const [deleteItem, { isSuccess: isDeleteSuccess }] =
    useDeleteReservationMutation();
  const [deleteSequentialItem, { isSuccess: isDeleteSequenceSuccess }] =
    useDeleteReservationSequenceMutation();
  const [changeReservationState, { isLoading: isChangingState }] =
    useGetReservationStateChangeInCmsMutation();
  const { data: allReservations, refetch: refetchReservations } =
    useGetReservationsQuery();
  const {
    deleteSuccess: showDeleteMessage,
    successMessage: showSuccessMessage,
    errorMessage,
  } = useToastMessages();

  // Import reservations
  const [importOpen, setImportOpen] = useState(false);
  const [
    importReservations,
    { data: importData, error: importError, reset: resetImport },
  ] = useImportReservationsMutation();

  useEffect(() => {
    document.title = t('Rezervácia miestností') + ' - FRI CMS';
  }, []);

  const listTitleMap = {
    all: t('Všetky rezervácie'),
    my: t('Moje rezervácie'),
    forApproval: t('Rezervácie čakajúce na schválenie'),
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      refetchReservations();
    }, Refresh_time);
    return () => clearInterval(intervalId);
  }, [refetchReservations]);

  const handleChangeState = (id, newState) => {
    if (!id) return;
    changeReservationState({ id, newState })
      .unwrap()
      .then(() => {
        const msg =
          newState === RESERVATION_STATUS.ACCEPTED.STATE
            ? t('Žiadosť o rezerváciu bola schválená.')
            : t('Žiadosť o rezerváciu bola zamietnutá.');
        showSuccessMessage({
          message: msg,
          description: t(
            'O výsledku spracovania žiadosti bude žiadateľ informovaný e-mailom.',
          ),
        });
        if (newState === RESERVATION_STATUS.ACCEPTED.STATE) setApproveId(null);
        if (newState === RESERVATION_STATUS.REJECTED.STATE) setRejectId(null);
        refetchReservations();
      });
  };

  const [selectedListOption, setSelectedListOption] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_FILTER_TITLE);
    return saved ?? 'all';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (
      tab === 'my' ||
      tab === 'all' ||
      (tab === 'forApproval' && canSeeForApproval)
    ) {
      setSelectedListOption(tab);
      sessionStorage.setItem(TABLE_FILTER_TITLE, tab);
      return;
    }
    if (params.has('forApproval') && canSeeForApproval) {
      setSelectedListOption('forApproval');
      sessionStorage.setItem(TABLE_FILTER_TITLE, 'forApproval');
    }
  }, [location.search, canSeeForApproval]);

  useEffect(() => {
    if (selectedListOption) {
      sessionStorage.setItem(TABLE_FILTER_TITLE, selectedListOption);
    }
  }, [selectedListOption]);

  useEffect(() => {
    if (selectedListOption === 'forApproval' && !canSeeForApproval) {
      setSelectedListOption('all');
    }
  }, [canSeeForApproval, selectedListOption]);

  const filteredReservations = useMemo(() => {
    if (!allReservations) return [];
    const effectiveTab =
      selectedListOption === 'forApproval' && !canSeeForApproval
        ? 'all'
        : selectedListOption;
    switch (effectiveTab) {
      case 'my':
        return allReservations.filter(
          (record) =>
            record.person_id === user?.id ||
            record.created_for_id === user?.id,
        );
      case 'forApproval':
        if (isRoomReservationsAdmin) {
          return allReservations.filter(
            (record) => record.state === RESERVATION_STATUS.WAITING.STATE,
          );
        }
        return allReservations.filter((record) => {
          const respIds =
            record.department_responsible_ids ||
            record.departmentResponsibleIds ||
            [];
          return (
            record.state === RESERVATION_STATUS.WAITING.STATE &&
            Array.isArray(respIds) &&
            respIds.includes(user?.id)
          );
        });
      case 'all':
      default:
        return allReservations;
    }
  }, [
    allReservations,
    selectedListOption,
    user,
    isRoomReservationsAdmin,
    canSeeForApproval,
  ]);

  const forApprovalCount = useMemo(() => {
    if (!allReservations) return 0;
    if (isRoomReservationsAdmin) {
      return allReservations.filter(
        (record) => record.state === RESERVATION_STATUS.WAITING.STATE,
      ).length;
    }
    return allReservations.filter((record) => {
      const respIds =
        record.department_responsible_ids ||
        record.departmentResponsibleIds ||
        [];
      return (
        record.state === RESERVATION_STATUS.WAITING.STATE &&
        Array.isArray(respIds) &&
        respIds.includes(user?.id)
      );
    }).length;
  }, [allReservations, user, isRoomReservationsAdmin]);

  const canApproveOrReject = (row) => {
    if (!row) return false;
    if (row.state !== RESERVATION_STATUS.WAITING.STATE) return false;
    if (isRoomReservationsAdmin) return true;
    const respIds =
      row.department_responsible_ids || row.departmentResponsibleIds || [];
    return Array.isArray(respIds) && respIds.includes(user?.id);
  };

  useEffect(() => {
    if (isDeleteSuccess) {
      showDeleteMessage();
      refetchReservations();
    }
  }, [isDeleteSuccess]);

  useEffect(() => {
    if (isDeleteSequenceSuccess) {
      showDeleteMessage();
      refetchReservations();
    }
  }, [isDeleteSequenceSuccess]);

  const handleApproveAll = async () => {
    if (isChangingState) return;
    const source =
      selectedListOption === 'forApproval'
        ? filteredReservations || []
        : allReservations || [];
    const approvables = source.filter((row) => canApproveOrReject(row));
    if (!approvables.length) {
      showSuccessMessage({ message: t('Nie je čo schváliť') });
      return;
    }
    const tasks = approvables.map((r) =>
      changeReservationState({
        id: r.id,
        newState: RESERVATION_STATUS.ACCEPTED.STATE,
      }).unwrap(),
    );
    await Promise.allSettled(tasks);
    showSuccessMessage({
      message: t('Všetky čakajúce žiadosti boli schválené'),
      description: t('Počet schválených žiadostí: {{count}}', {
        count: approvables.length,
      }),
    });
    refetchReservations();
  };

  const openApproveAllConfirm = () => {
    if (isChangingState) return;
    const source =
      selectedListOption === 'forApproval'
        ? filteredReservations || []
        : allReservations || [];
    const approvables = source.filter((row) => canApproveOrReject(row));
    if (!approvables.length) {
      showSuccessMessage({ message: t('Nie je čo schváliť') });
      return;
    }
    setConfirmApproveAllVisible(true);
  };

  const handleImportSubmit = (values) => {
    importReservations({ body: values })
      .unwrap()
      .then((response) => {
        // Errors may come as a top-level array of strings or as { error: string[] }
        const errorsArray = Array.isArray(response)
          ? response
          : Array.isArray(response?.error)
            ? response.error
            : [];

        if (!errorsArray.length) {
          showSuccessMessage({ message: t('Import úspešne vykonaný') });
          setImportOpen(false);
          refetchReservations();
        } else {
          // Keep modal open and show alert with errors via validationErrors prop from mutation state
          errorMessage({
            message: t('Import neúspešný'),
            description: t('V importovanom súbore je chyba.'),
            key: IMPORT_ERROR_TOAST_KEY,
          });
        }
      })
      .catch(() => {
        // Always show a generic import failed toast; detailed errors are rendered below
        errorMessage({
          message: t('Import neúspešný'),
          description: t('V importovanom súbore je chyba.'),
          key: IMPORT_ERROR_TOAST_KEY,
        });
      });
  };

  if (!allReservations) {
    return <Spinner />;
  }

  const customSettings = (row) => (
    <div style={{ display: 'flex', gap: 6 }}>
      {selectedListOption === 'all' && isRoomReservationsAdmin && (
        <IconButton
          icon={<EditOutlined />}
          tooltip={t('Upraviť')}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${routes.cmsReservationsEdit}/${row.id}`);
          }}
        />
      )}

      {selectedListOption === 'forApproval' &&
        canSeeForApproval &&
        row.state === RESERVATION_STATUS.WAITING.STATE &&
        !row.isChild && (
          <>
            <IconButton
              icon={
                row.rr_reservation_sequence_id ? (
                  <CheckCircleOutlined />
                ) : (
                  <CheckOutlined />
                )
              }
              tooltip={
                row.rr_reservation_sequence_id
                  ? t('Schváliť žiadosť s opakovaním')
                  : t('Schváliť žiadosť')
              }
              disabled={isChangingState || !canApproveOrReject(row)}
              onClick={(e) => {
                e.stopPropagation();
                if (!canApproveOrReject(row)) return;
                setApproveIsSequence(Boolean(row.rr_reservation_sequence_id));
                setApproveId(row.id);
              }}
            />
            <IconButton
              icon={
                row.rr_reservation_sequence_id ? (
                  <CloseCircleOutlined />
                ) : (
                  <CloseOutlined />
                )
              }
              tooltip={
                row.rr_reservation_sequence_id
                  ? t('Zamietnuť žiadosť s opakovaním')
                  : t('Zamietnuť žiadosť')
              }
              disabled={isChangingState || !canApproveOrReject(row)}
              onClick={(e) => {
                e.stopPropagation();
                if (!canApproveOrReject(row)) return;
                setRejectIsSequence(Boolean(row.rr_reservation_sequence_id));
                setRejectId(row.id);
              }}
            />
          </>
        )}

      {(user.id === row.person_id ||
        user.id === row.created_for_id ||
        isRoomReservationsAdmin) &&
        !row.rr_reservation_sequence_id && (
          <IconButton
            icon={<DeleteFilled />}
            tooltip={t('Vymazať rezerváciu')}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteIndex(row.id);
            }}
          />
        )}

      {(user.id === row.person_id ||
        user.id === row.created_for_id ||
        isRoomReservationsAdmin) &&
        row.rr_reservation_sequence_id &&
        !row.isChild && (
          <IconButton
            icon={<DeleteOutlined />}
            tooltip={t('Vymazať rezerváciu s opakovaním')}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteSequentialIndex(row.rr_reservation_sequence_id);
            }}
          />
        )}
    </div>
  );

  return (
    <>
      {importOpen && (
        <ReservationsImport
          toggle={() => {
            setImportOpen(false);
            resetImport();
          }}
          validationErrors={
            Array.isArray(importError?.data)
              ? importError.data
              : Array.isArray(importData)
                ? importData
                : []
          }
          onSubmit={handleImportSubmit}
        />
      )}

      <AppBarCMS
        title={t('Rezervácie miestností')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať žiadosť o rezerváciu')}
        createUrl={routes.cmsReservationsCreate}
        permissionsBasic="reservations"
        mainActions={[
          {
            permission: 'reservations-sequence.store',
            onClick: () => navigate(routes.cmsReservationSequenceCreate),
            title: t('Pridať žiadosť o rezerváciu s opakovaním'),
            icon: <PlusSquareOutlined />,
          },
          {
            permission: 'reservations-import',
            onClick: () => setImportOpen(true),
            title: t('Importovať rezervácie'),
            icon: <UploadOutlined />,
          },
          ...(selectedListOption === 'forApproval' && canSeeForApproval
            ? [
                {
                  permission: 'reservation-responsible',
                  onClick: openApproveAllConfirm,
                  title: t('Schváliť všetky čakajúce žiadosti'),
                  icon: <CheckCircleOutlined />,
                },
              ]
            : []),
        ]}
      />

      <div style={{ margin: '10px' }}>
        <TableFilter
          title={t(TABLE_FILTER_TITLE)}
          value={selectedListOption}
          setValue={(val) => {
            if (val === 'forApproval' && !canSeeForApproval) return;
            setSelectedListOption(val);
          }}
          options={[
            { value: 'my', label: listTitleMap.my, count: undefined },
            { value: 'all', label: listTitleMap.all, count: undefined },
            ...(canSeeForApproval
              ? [
                  {
                    value: 'forApproval',
                    label: listTitleMap.forApproval,
                    count: forApprovalCount,
                  },
                ]
              : []),
          ]}
        />

        <CollapseBlock
          title={
            listTitleMap[
              selectedListOption === 'forApproval' && !canSeeForApproval
                ? 'all'
                : selectedListOption
            ]
          }
        >
          <ReservationsTable
            reservations={filteredReservations}
            customSettings={customSettings}
            user={user}
            isRoomReservationsAdmin={isRoomReservationsAdmin}
            fromApproval={
              selectedListOption === 'forApproval' && canSeeForApproval
            }
          />
        </CollapseBlock>
      </div>

      <QuestionModal
        chosenPositive={() =>
          handleChangeState(approveId, RESERVATION_STATUS.ACCEPTED.STATE)
        }
        question={
          approveIsSequence
            ? t(
                'Chcete naozaj schváliť túto žiadosť o rezerváciu s opakovaním? Automaticky sa schvália všetky rezervácie, ktoré sú súčasťou opakovania.',
              )
            : t('Chcete naozaj schváliť túto žiadosť o rezerváciu?')
        }
        visible={approveId !== null}
        yes={t('Áno')}
        toggle={() => {
          setApproveId(null);
          setApproveIsSequence(false);
        }}
        headerTitle={
          approveIsSequence
            ? t('Schváliť žiadosť o rezerváciu s opakovaním')
            : t('Schváliť žiadosť o rezerváciu')
        }
      />

      <QuestionModal
        chosenPositive={() =>
          handleChangeState(rejectId, RESERVATION_STATUS.REJECTED.STATE)
        }
        question={
          rejectIsSequence
            ? t(
                'Chcete naozaj zamietnuť túto žiadosť o rezerváciu s opakovaním? Automaticky sa zamietnu všetky rezervácie, ktoré sú súčasťou opakovania.',
              )
            : t('Chcete naozaj zamietnuť túto žiadosť o rezerváciu?')
        }
        visible={rejectId !== null}
        yes={t('Áno')}
        toggle={() => {
          setRejectId(null);
          setRejectIsSequence(false);
        }}
        headerTitle={
          rejectIsSequence
            ? t('Zamietnuť žiadosť o rezerváciu s opakovaním')
            : t('Zamietnuť žiadosť o rezerváciu')
        }
      />

      <QuestionModal
        chosenPositive={() => deleteItem(confirmDeleteIndex)}
        question={t('Naozaj chcete vymazať túto rezerváciu?')}
        visible={confirmDeleteIndex !== DELETE_CONFIRM_CLOSE}
        yes={t('Áno')}
        toggle={() => setDeleteIndex(DELETE_CONFIRM_CLOSE)}
        headerTitle={t('Vymazať položku')}
      />

      <QuestionModal
        chosenPositive={() =>
          deleteSequentialItem(confirmDeleteSequentialIndex)
        }
        question={t(
          'Naozaj chcete vymazať túto rezerváciu s opakovaním? Vymažú sa všetky rezervácie, ktoré sú súčasťou opakovania.',
        )}
        visible={confirmDeleteSequentialIndex !== DELETE_CONFIRM_CLOSE}
        yes={t('Áno')}
        toggle={() => setDeleteSequentialIndex(DELETE_CONFIRM_CLOSE)}
        headerTitle={t('Vymazať položku')}
      />

      <QuestionModal
        chosenPositive={handleApproveAll}
        question={t('Chcete naozaj schváliť všetky čakajúce žiadosti?')}
        visible={confirmApproveAllVisible}
        yes={t('Áno')}
        toggle={() => setConfirmApproveAllVisible(false)}
        headerTitle={t('Schváliť všetky žiadosti')}
      />
    </>
  );
};

export default ReservationsPage;
